import { Injectable } from '@angular/core';
import { Scheme } from '@app/modules/configuration/configuration.types';
import { SchemesService } from '@core/services/schemes.service';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { switchMap } from 'rxjs';
import { delay, tap } from 'rxjs/operators';
import { Logger } from '../../services/logger.service';
import {
  ActivateSchemeAction,
  DeactivateSchemeAction,
  GenerateSchemeAction,
  GetSchemeFieldsAction,
  LoadSchemeAction,
  LoadSchemesAction,
  SetSchemesAction,
  UpdateSchemeAction,
} from './schemes.actions';

const SCHEMES_STATE_NAME = 'schemes';

export interface SchemesStateModel {
  schemes: Scheme[];
  fields: { [AnalysisType: string]: string[] };
}

@State<SchemesStateModel>({
  name: SCHEMES_STATE_NAME,
  defaults: {
    schemes: [],
    fields: {},
  },
})
@Injectable()
export class SchemeState {
  private log = new Logger('SchemeState');

  constructor(private schemesService: SchemesService) {}

  @Selector()
  static getActiveSchemes(state: SchemesStateModel) {
    return state.schemes.filter((scheme) => !scheme.deactivated);
  }

  @Selector()
  static getUnactiveSchemes(state: SchemesStateModel) {
    return state.schemes.filter((scheme) => !!scheme.deactivated);
  }

  @Selector()
  static getAllSchemesList(state: SchemesStateModel) {
    return state.schemes;
  }

  @Selector()
  static getSchemeById(state: SchemesStateModel) {
    return (schemeId: string) => state.schemes.find((ac) => ac.id === schemeId);
  }

  @Selector()
  static getFields(state: SchemesStateModel) {
    return state.fields;
  }

  @Selector()
  static getFieldsByType(state: SchemesStateModel) {
    return (type: string) => {
      const normalizedType = type?.toLowerCase();
      const matchingKey = Object.keys(state.fields).find(
        (key) => key.toLowerCase() === normalizedType
      );

      return (matchingKey && state.fields[matchingKey]) || [];
    };
  }

  @Action(LoadSchemesAction)
  loadSchemesList({ dispatch }: StateContext<SchemesStateModel>) {
    return this.schemesService
      .listSchemes(null, {})
      .pipe(
        switchMap((schemes) => dispatch(new SetSchemesAction(schemes.items)))
      );
  }

  @Action(LoadSchemeAction)
  loadScheme(
    { dispatch }: StateContext<SchemesStateModel>,
    { id }: LoadSchemeAction
  ) {
    return this.schemesService
      .getScheme(id)
      .pipe(switchMap((scheme) => dispatch(new SetSchemesAction([scheme]))));
  }

  @Action(SetSchemesAction)
  setSchemes(
    { patchState, getState }: StateContext<SchemesStateModel>,
    { schemes }: SetSchemesAction
  ) {
    const filteredSchemes = [...getState().schemes].filter(
      (scheme) => !schemes.find((newScheme) => newScheme.id === scheme.id)
    );
    return patchState({
      schemes: filteredSchemes
        .concat(schemes)
        .sort((a, b) => (a.name.toLowerCase() > b.name.toLowerCase() ? 1 : -1)),
    });
  }

  @Action(GenerateSchemeAction)
  generateScheme(
    { dispatch }: StateContext<SchemesStateModel>,
    { scheme }: GenerateSchemeAction
  ) {
    return this.schemesService.createScheme(scheme).pipe(
      delay(300),
      switchMap(() => dispatch(new LoadSchemeAction(scheme.id)))
    );
  }

  @Action(UpdateSchemeAction)
  updateScheme(
    { dispatch }: StateContext<SchemesStateModel>,
    { scheme }: UpdateSchemeAction
  ) {
    return this.schemesService.updateScheme(scheme).pipe(
      delay(300),
      switchMap(() => dispatch(new LoadSchemeAction(scheme.id)))
    );
  }

  @Action(ActivateSchemeAction)
  activateScheme(
    { dispatch }: StateContext<SchemesStateModel>,
    { id }: ActivateSchemeAction
  ) {
    return this.schemesService
      .activateScheme(id)
      .pipe(switchMap(() => dispatch(new LoadSchemeAction(id))));
  }

  @Action(DeactivateSchemeAction)
  deactivateScheme(
    { dispatch }: StateContext<SchemesStateModel>,
    { id }: DeactivateSchemeAction
  ) {
    return this.schemesService
      .deactivateScheme(id)
      .pipe(switchMap(() => dispatch(new LoadSchemeAction(id))));
  }

  @Action(GetSchemeFieldsAction)
  getSchemeFields({ patchState }: StateContext<SchemesStateModel>) {
    return this.schemesService
      .getFields()
      .pipe(tap((fields) => patchState({ fields })));
  }
}
